import React, { useEffect, useState } from 'react';
import { APIProvider, Map, useMap } from '@vis.gl/react-google-maps';
import { ClusteredDeviceMarkers } from './clustered_device_markers';
import { DistrictMarkers } from './district_markers';
import OverlayParent from './overlays_parent';
import { useDispatch, useSelector } from 'react-redux';
import { displayDevices, displayDistricts } from '../../redux/mapStateSlice';
import ImageMapContent from './image_map_content';
import { calculateImageBounds } from '../utils/imageBounds';

// New child component that handles map operations
const MapContent = ({ zoomedDevices, displayedDevices, displayedDistricts, mapSettings, restrictionBounds }) => {
  const map = useMap();
  const hasInitiallyZoomed = React.useRef(false);

  // Zoom on explicit request (clicking a district, etc.)
  useEffect(() => {
    if (!map || mapSettings.display_type === 'image') return;
    if (zoomedDevices?.length > 0) {
      const bounds = new window.google.maps.LatLngBounds();
      zoomedDevices.forEach((item) => bounds.extend({ lat: item.lat, lng: item.lng }));
      map.fitBounds(bounds, { top: 15, bottom: 15, left: 15, right: 15 });
    }
  }, [zoomedDevices, map]);

  // Initial zoom when data first becomes available
  useEffect(() => {
    if (!map || mapSettings.display_type === 'image' || hasInitiallyZoomed.current) return;
    const items = displayedDevices?.length > 0 ? displayedDevices : displayedDistricts;
    if (items?.length > 0) {
      // Small delay to ensure map tiles are ready
      setTimeout(() => {
        const bounds = new window.google.maps.LatLngBounds();
        items.forEach((item) => bounds.extend({ lat: item.lat, lng: item.lng }));
        map.fitBounds(bounds, { top: 15, bottom: 15, left: 15, right: 15 });
        hasInitiallyZoomed.current = true;
      }, 200);
    }
  }, [displayedDevices, displayedDistricts, map, mapSettings]);

  return (
    <>
      <ClusteredDeviceMarkers />
      <DistrictMarkers />
    </>
  );
};

const MultiMap = () => {
  const [restrictionBounds, setRestrictionBounds] = useState(null);
  const dispatch = useDispatch();
  const deviceData = useSelector((state) => state.devices.devices);
  const districtData = useSelector((state) => state.districts.districts);
  const mapMode = useSelector((state) => state.mapState.mapMode);
  const zoomedDevices = useSelector((state) => state.mapState.zoomedDevices);
  const displayedDevices = useSelector((state) => state.mapState.displayedDevices);
  const displayedDistricts = useSelector((state) => state.mapState.displayedDistricts);
  const mapSettings = useSelector((state) => state.settings);

  useEffect(() => {
    if (mapMode === 'districts' && districtData?.length > 0) {
      dispatch(displayDistricts(districtData));
    } else if (mapMode === 'citywide' && deviceData?.length > 0) {
      dispatch(displayDevices(deviceData));
    }
  }, [deviceData, districtData, mapMode]);

  useEffect(() => {
    let isCancelled = false;

    const imageUrl = mapSettings.image_display_settings.image_url;
    if (!imageUrl) {
      return;
    }

    calculateImageBounds(imageUrl)
      .then(bounds => {
        if (!isCancelled) {
          console.log("Calculated bounds:", bounds);
          console.log(imageUrl);
          setRestrictionBounds(bounds);
        }
      })
      .catch(error => {
        if (!isCancelled) {
          console.error("Error loading image:", error);
        }
      });

    return () => {
      isCancelled = true;
    };
  }, [mapSettings]);

  return (
      <div className="relative h-full w-full">
        {mapSettings.display_type === 'map' ? ( // type map
          <Map
            defaultZoom={mapSettings.map_display_settings.zoom}
            defaultCenter={mapSettings.map_display_settings.center}
            mapId={mapSettings.map_display_settings.mapId}
            mapTypeId="hybrid"
            mapTypeControlOptions={{
              position: 20,
              style: 1,
            }}
            options={{
              disableDefaultUI: false,
              zoomControl: true,
              streetViewControl: true,
              fullscreenControl: false,
            }}
            gestureHandling={'greedy'}
            className="w-full h-full"
          >
            <MapContent
              zoomedDevices={zoomedDevices}
              displayedDevices={displayedDevices}
              displayedDistricts={displayedDistricts}
              mapSettings={mapSettings}
              restrictionBounds={restrictionBounds}
            />
          </Map>
        ) : (
          // type image
          <Map
            defaultCenter={{ lat: 0, lng: 0 }} // Set to coordinates 0, 0
            defaultBounds={{north: 0.003, south: -0.003, east: 0.003, west: -0.003}}
            mapTypeControlOptions={{
              position: 20,
              style: 1,
            }}
            options={{
              disableDefaultUI: true,
              zoomControl: true,
              streetViewControl: false,
              fullscreenControl: false,
              restriction: {
                latLngBounds: {
                  north: 0.006,
                  south: -0.006,
                  east: 0.006,
                  west: -0.006,
                },
                strictBounds: false,
              }
            }}
            mapId={"a39b4ad57206b5ef"} // blank map id
            gestureHandling={'greedy'}
            className="w-full h-full"
          >
            <ImageMapContent restrictionBounds={restrictionBounds} mapSettings={mapSettings} />
            <MapContent
              zoomedDevices={zoomedDevices}
              displayedDevices={displayedDevices}
              displayedDistricts={displayedDistricts}
              mapSettings={mapSettings}
              restrictionBounds={restrictionBounds}
            />
          </Map>
        )}
        <OverlayParent />
      </div>
  );
};

export default MultiMap;
